import { useState } from "react";
import { Box, Chip, IconButton, Popover, Tooltip, Typography } from "@mui/material";
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  AutoAwesome as AiIcon,
  DeleteOutline as DeleteIcon,
  VerifiedOutlined as VerifiedIcon,
  HelpOutline as InferredIcon,
  LocationOnOutlined as LocationIcon,
} from "@mui/icons-material";
import PillActionButton from "./common/PillActionButton";
import { formatPhoneNumber } from "../../utils/phoneFormat";

const SELECTED_BORDER = "#EAB308";
const SELECTED_BG = "rgba(234, 179, 8, 0.08)";
const SELECTED_SHADOW =
  "0 0 0 1px rgba(234, 179, 8, 0.8), 0 10px 22px rgba(234, 179, 8, 0.25)";
const CARD_ACCENT = "#3B82F6";
const LINK_BLUE = "#2563EB";

const STATE_STYLES = {
  queued: { bg: "#FEF3C7", fg: "#92400E", border: "#FCD34D", label: "Queued" },
  sent: { bg: "#DCFCE7", fg: "#166534", border: "#86EFAC", label: "Sent" },
  failed: { bg: "#FEE2E2", fg: "#991B1B", border: "#FCA5A5", label: "Failed" },
};

const formatStateTimestamp = (value) => {
  if (value == null || value === "") return "";
  const num = Number(value);
  const date = Number.isFinite(num) && String(num).length >= 10 ? new Date(num) : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const today = new Date();
  const sameDay =
    date.getFullYear() === today.getFullYear()
    && date.getMonth() === today.getMonth()
    && date.getDate() === today.getDate();
  if (sameDay) {
    return date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  }
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const formatUpdatedAtTiny = (value) => {
  if (value == null || value === "") return "";
  const num = Number(value);
  const date = Number.isFinite(num) && String(num).length >= 10 ? new Date(num) : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const pillBaseSx = {
  height: 18,
  px: 0.75,
  borderRadius: "999px",
  fontSize: 10,
  fontWeight: 700,
  lineHeight: "16px",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
  display: "inline-flex",
  alignItems: "center",
};

export const contactCardSx = {
  card: (selected, categoryBgColor, statusBarColor) => ({
    position: "relative",
    border: "1px solid",
    borderColor: selected ? SELECTED_BORDER : "#E5E7EB",
    borderRadius: "10px",
    p: 1,
    pl: 1.5,
    backgroundColor: categoryBgColor || (selected ? SELECTED_BG : "#FFFFFF"),
    cursor: "pointer",
    boxShadow: selected ? SELECTED_SHADOW : "0 2px 10px rgba(0,0,0,0.08)",
    transition: "all 0.15s cubic-bezier(0.4,0,0.2,1)",
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 10,
      bottom: 10,
      width: selected ? 5 : 4,
      borderRadius: 8,
      backgroundColor: statusBarColor || (selected ? SELECTED_BORDER : CARD_ACCENT),
    },
    "&:hover": {
      borderColor: selected ? SELECTED_BORDER : "#CBD5E1",
      backgroundColor: selected ? SELECTED_BG : "#F8FAFC",
      boxShadow: selected
        ? "0 12px 28px rgba(0,0,0,0.22)"
        : "0 3px 12px rgba(0,0,0,0.12)",
      transform: "translateY(-1px)",
    },
  }),
  cardHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 0.75 },
  statusChip: (bg, fg) => ({
    ...pillBaseSx,
    border: "1px solid rgba(0,0,0,0.12)",
    backgroundColor: bg || "#E2E8F0",
    color: fg || "#1F2937",
    flexShrink: 0,
  }),
  cardName: { fontSize: 14, fontWeight: 700, color: "#111827", lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  cardTitle: { fontSize: 11, color: "#6B7280", lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  cardCompany: { fontSize: 11, color: LINK_BLUE, fontWeight: 500, lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  contactRow: { display: "flex", alignItems: "center", gap: 0.75, mt: 0.4 },
  contactIcon: { fontSize: 14, color: "#6B7280", flexShrink: 0 },
  contactLink: { fontSize: 12, color: LINK_BLUE, fontWeight: 500, textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", "&:hover": { textDecoration: "underline" } },
  cardFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    mt: 0.7,
    pt: 0.5,
    gap: 0.5,
    borderTop: "1px solid rgba(148,163,184,0.2)",
  },
  categoryPill: (clickable) => ({
    ...pillBaseSx,
    border: "1px solid #E2E8F0", backgroundColor: "#F8FAFC",
    color: "#475569", overflow: "hidden", textOverflow: "ellipsis",
    maxWidth: "60%", cursor: clickable ? "pointer" : "default",
  }),
  iconBtn: { width: 22, height: 22, color: "#94A3B8", "&:hover": { backgroundColor: "#FEE2E2", color: "#EF4444" } },
};

const ROLE_COLORS = {
  owner: { bg: "#FEF3C7", color: "#92400E", border: "#FCD34D" },
  "fee-title": { bg: "#FED7AA", color: "#7C2D12", border: "#FB923C" },
  broker: { bg: "#DBEAFE", color: "#1E40AF", border: "#93C5FD" },
  agent: { bg: "#E0E7FF", color: "#3730A3", border: "#A5B4FC" },
  tenant: { bg: "#DCFCE7", color: "#166534", border: "#86EFAC" },
  lender: { bg: "#FCE7F3", color: "#9D174D", border: "#F9A8D4" },
  manager: { bg: "#F3E8FF", color: "#6B21A8", border: "#D8B4FE" },
  default: { bg: "#F1F5F9", color: "#475569", border: "#CBD5E1" },
};

export const RoleChip = ({ role }) => {
  if (!role) return null;
  const palette = ROLE_COLORS[String(role).toLowerCase()] || ROLE_COLORS.default;
  return (
    <Chip
      size="small"
      label={role}
      sx={{
        height: 18, fontSize: 10, fontWeight: 700, borderRadius: "999px",
        backgroundColor: palette.bg, color: palette.color, border: `1px solid ${palette.border}`,
        "& .MuiChip-label": { px: 0.75 },
        textTransform: "uppercase", letterSpacing: "0.04em",
      }}
    />
  );
};

const CONFIDENCE_COLORS = {
  verified: "#16A34A", high: "#16A34A",
  inferred: "#D97706", medium: "#D97706",
  low: "#DC2626",
};

export const ProvenanceBadge = ({ source, confidence }) => {
  if (!source && !confidence) return null;
  const key = String(confidence || "").toLowerCase();
  const color = CONFIDENCE_COLORS[key] || "#64748B";
  const Icon = key === "verified" || key === "high" ? VerifiedIcon : InferredIcon;
  const title = (
    <Box sx={{ fontSize: 11, lineHeight: 1.35 }}>
      {confidence && <Box sx={{ fontWeight: 700, textTransform: "capitalize" }}>{confidence}</Box>}
      {source && <Box sx={{ opacity: 0.9 }}>{source}</Box>}
    </Box>
  );
  return (
    <Tooltip title={title} arrow placement="top">
      <Box component="span" sx={{ display: "inline-flex", ml: 0.5, color, cursor: "help" }} onClick={(e) => e.stopPropagation()}>
        <Icon sx={{ fontSize: 13 }} />
      </Box>
    </Tooltip>
  );
};

export const PhoneRow = ({ phone, source, confidence, onClick, onCompose }) => {
  if (!phone) return null;
  const telHref = `tel:${String(phone).replace(/\D/g, "")}`;
  return (
    <Box sx={contactCardSx.contactRow}>
      <PhoneIcon sx={contactCardSx.contactIcon} />
      <Typography
        component="a"
        href={telHref}
        onClick={(e) => {
          e.stopPropagation();
          if (onClick) {
            e.preventDefault();
            onClick(phone);
          }
        }}
        sx={contactCardSx.contactLink}
      >
        {formatPhoneNumber(phone)}
      </Typography>
      <ProvenanceBadge source={source} confidence={confidence} />
      {onCompose && (
        <PillActionButton
          icon={<AiIcon />}
          label="Compose"
          onClick={(e) => { e.stopPropagation(); onCompose(); }}
          sx={{ ml: "auto", flexShrink: 0 }}
        />
      )}
    </Box>
  );
};

export const EmailRow = ({ email, source, confidence, onCompose }) => {
  if (!email) return null;
  return (
    <Box sx={contactCardSx.contactRow}>
      <EmailIcon sx={contactCardSx.contactIcon} />
      <Typography
        component="a"
        href={`mailto:${email}`}
        onClick={(e) => e.stopPropagation()}
        sx={{ ...contactCardSx.contactLink, minWidth: 0 }}
      >
        {email}
      </Typography>
      <ProvenanceBadge source={source} confidence={confidence} />
      {onCompose && (
        <PillActionButton
          icon={<AiIcon />}
          label="Compose"
          onClick={(e) => { e.stopPropagation(); onCompose(); }}
          sx={{ ml: "auto", flexShrink: 0 }}
        />
      )}
    </Box>
  );
};

const findCategory = (categories, value) => {
  if (!value || !Array.isArray(categories)) return null;
  return categories.find(
    (c) => c?.id === value || c?.value === value || c?.name === value || c?.label === value
  );
};

const findStatus = (statuses, value) => {
  if (!value || !Array.isArray(statuses)) return null;
  return statuses.find(
    (s) => s?.id === value || s?.value === value || s?.name === value || s?.label === value
  );
};

const normalizeTitleText = (value) =>
  String(value || "")
    .trim()
    .replace(/^[,\s;:-]+/, "")
    .trim();

const lightenHexColor = (color, amount = 0.1) => {
  const hex = String(color || "").trim();
  const match = hex.match(/^#([0-9a-fA-F]{6})$/);
  if (!match) return color || "";
  const raw = match[1];
  const r = parseInt(raw.slice(0, 2), 16);
  const g = parseInt(raw.slice(2, 4), 16);
  const b = parseInt(raw.slice(4, 6), 16);
  const mix = (v) => Math.round(v + (255 - v) * amount);
  const toHex = (v) => v.toString(16).padStart(2, "0");
  return `#${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`;
};

const AddressRow = ({ address }) => {
  if (!address) return null;
  return (
    <Box sx={contactCardSx.contactRow}>
      <LocationIcon sx={contactCardSx.contactIcon} />
      <Typography
        sx={{ ...contactCardSx.contactLink, color: "#475569", cursor: "default", "&:hover": { textDecoration: "none" } }}
      >
        {address}
      </Typography>
    </Box>
  );
};

const ContactCard = ({
  contact,
  role,
  selected,
  showAddress = false,
  categories = [],
  statuses = [],
  onClick,
  onDoubleClick,
  onPhoneClick,
  onCompose,
  onComposeText,
  onCategoryChange,
  onStatusChange,
  onDelete,
  onViewLinkedProperties,
}) => {
  const personName = `${contact?.firstName || ""} ${contact?.lastName || ""}`.trim();
  const normalized = (v) => String(v || "").trim().toLowerCase();
  const primaryLine = personName || contact?.company || "";
  const isCompanyOnly = !personName && Boolean(contact?.company);
  const personTitle = normalizeTitleText(contact?.personTitle);
  const companyType = normalizeTitleText(contact?.companyTitle);
  const showPersonTitleLine = Boolean(personTitle);
  const showCompanyTypeLine =
    Boolean(companyType) &&
    normalized(companyType) !== normalized(contact?.company);

  const [categoryAnchor, setCategoryAnchor] = useState(null);
  const [statusAnchor, setStatusAnchor] = useState(null);
  const activeCategories = Array.isArray(categories)
    ? categories.filter((c) => c?.isActive !== false)
    : [];
  const activeStatuses = Array.isArray(statuses)
    ? statuses.filter((s) => s?.isActive !== false)
    : [];
  const currentCategory = findCategory(categories, contact?.category);
  const categoryLabel = currentCategory?.label || currentCategory?.name || contact?.category || "Set Contact Type";
  const currentStatus = findStatus(statuses, contact?.status);
  const categoryBgColor = lightenHexColor(currentCategory?.bgColor || currentCategory?.color || "", 0.55);
  const statusBarColor = currentStatus?.bgColor || currentStatus?.color || "";
  const statusLabel = currentStatus?.label || currentStatus?.name || currentStatus?.value || contact?.status || "";
  const statusTextColor = currentStatus?.textColor || "";

  const handleCategoryClick = (e) => {
    e.stopPropagation();
    if (!onCategoryChange) return;
    setCategoryAnchor(e.currentTarget);
  };

  const handlePick = (cat) => {
    setCategoryAnchor(null);
    onCategoryChange?.(contact, cat);
  };

  const handleStatusClick = (e) => {
    e.stopPropagation();
    if (!onStatusChange) return;
    setStatusAnchor(e.currentTarget);
  };

  const handlePickStatus = (statusRow) => {
    setStatusAnchor(null);
    const nextValue =
      statusRow?.value || statusRow?.id || statusRow?.name || statusRow?.label || "";
    onStatusChange?.(contact, nextValue);
  };

  return (
    <Box data-testid="contact-card" data-contact-id={contact?.id} sx={contactCardSx.card(selected, categoryBgColor, statusBarColor)} onClick={onClick} onDoubleClick={onDoubleClick}>
      <Box sx={contactCardSx.cardHeader}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={contactCardSx.cardName}>{primaryLine || "—"}</Typography>
          {showPersonTitleLine && <Typography sx={contactCardSx.cardTitle}>{personTitle}</Typography>}
          {contact?.company && !isCompanyOnly && (
            <Typography sx={contactCardSx.cardCompany}>{contact.company}</Typography>
          )}
          {showCompanyTypeLine && <Typography sx={contactCardSx.cardTitle}>{companyType}</Typography>}
        </Box>
        {statusLabel && (
          <Box
            sx={{ ...contactCardSx.statusChip(statusBarColor, statusTextColor), cursor: onStatusChange ? "pointer" : "default" }}
            onClick={handleStatusClick}
            title={onStatusChange ? "Change status" : undefined}
          >
            {statusLabel}
          </Box>
        )}
        {!statusLabel && role && <RoleChip role={role} />}
      </Box>
      <PhoneRow
        phone={contact?.phone}
        source={contact?.phoneSource}
        confidence={contact?.phoneConfidence}
        onClick={onPhoneClick}
        onCompose={onComposeText}
      />
      <EmailRow
        email={contact?.email}
        source={contact?.emailSource}
        confidence={contact?.emailConfidence}
        onCompose={onCompose}
      />
      {showAddress && <AddressRow address={contact?.address} />}
      <Box sx={contactCardSx.cardFooter}>
        <Box
          sx={contactCardSx.categoryPill(Boolean(onCategoryChange))}
          onClick={handleCategoryClick}
        >
          {categoryLabel}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, ml: "auto", flexWrap: "wrap", justifyContent: "flex-end" }}>
          {Number(contact?.propertyCount) > 0 ? (
            <Tooltip
              title={
                onViewLinkedProperties
                  ? `Click to filter to all ${contact.propertyCount} linked ${contact.propertyCount === 1 ? "property" : "properties"}`
                  : `Linked to ${contact.propertyCount} ${contact.propertyCount === 1 ? "property" : "properties"}`
              }
            >
              <Box
                onClick={onViewLinkedProperties ? (e) => { e.stopPropagation(); onViewLinkedProperties(contact); } : undefined}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  px: 0.75,
                  py: 0.15,
                  borderRadius: 999,
                  border: "1px solid #C7D2FE",
                  backgroundColor: "#EEF2FF",
                  color: "#3730A3",
                  fontSize: 10,
                  fontWeight: 700,
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                  cursor: onViewLinkedProperties ? "pointer" : "default",
                  "&:hover": onViewLinkedProperties ? { backgroundColor: "#E0E7FF", borderColor: "#A5B4FC" } : undefined,
                }}
              >
                {contact.propertyCount} {contact.propertyCount === 1 ? "prop" : "props"}
              </Box>
            </Tooltip>
          ) : null}
          {Array.isArray(contact?.sourceLabels) && contact.sourceLabels.map((label) => (
            <Tooltip key={label} title={`Source: ${label}`}>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  px: 0.75,
                  py: 0.15,
                  borderRadius: 999,
                  border: "1px solid #CBD5E1",
                  backgroundColor: "#F1F5F9",
                  color: "#334155",
                  fontSize: 10,
                  fontWeight: 600,
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                }}
              >
                {label}
              </Box>
            </Tooltip>
          ))}
          {formatUpdatedAtTiny(contact?.updatedAt) && (
            <Typography sx={{ fontSize: 10, color: "#64748B", lineHeight: 1, whiteSpace: "nowrap" }}>
              {formatUpdatedAtTiny(contact?.updatedAt)}
            </Typography>
          )}
          {(() => {
            const key = String(contact?.state || "").toLowerCase();
            const style = STATE_STYLES[key];
            if (!style) return null;
            const ts = formatStateTimestamp(contact?.updatedAt);
            return (
              <Tooltip title={ts ? `${style.label} · ${new Date(Number(contact.updatedAt) || contact.updatedAt).toLocaleString()}` : style.label}>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 0.75,
                    py: 0.15,
                    borderRadius: 999,
                    border: `1px solid ${style.border}`,
                    backgroundColor: style.bg,
                    color: style.fg,
                    fontSize: 10,
                    fontWeight: 600,
                    lineHeight: 1.2,
                    whiteSpace: "nowrap",
                  }}
                >
                  <span>{style.label}</span>
                  {ts && <span style={{ opacity: 0.75, fontWeight: 500 }}>· {ts}</span>}
                </Box>
              </Tooltip>
            );
          })()}
          {onDelete && (
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onDelete(contact); }}
              sx={contactCardSx.iconBtn}
            >
              <DeleteIcon sx={{ fontSize: 13 }} />
            </IconButton>
          )}
        </Box>
      </Box>
      <Popover
        open={Boolean(statusAnchor)}
        anchorEl={statusAnchor}
        onClose={() => setStatusAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box sx={{ minWidth: 200, p: 0.75 }}>
          <Typography
            sx={{
              fontSize: 11.5, fontWeight: 700, color: "#475569",
              px: 0.75, py: 0.5, borderBottom: "1px solid #E2E8F0", mb: 0.5,
            }}
          >
            Change Status
          </Typography>
          {activeStatuses.map((s) => {
            const current = String(contact?.status || "").trim();
            const v = String(s?.value || s?.id || s?.name || s?.label || "").trim();
            const selectedStatus = current && v && current.toLowerCase() === v.toLowerCase();
            return (
              <Box
                key={`status-${s.id || s.value || s.name || s.label}`}
                onClick={() => handlePickStatus(s)}
                sx={{
                  display: "flex", alignItems: "center", gap: 0.75,
                  px: 1, py: 0.75, borderRadius: 1,
                  fontSize: 12.5, fontWeight: selectedStatus ? 700 : 500,
                  cursor: "pointer",
                  backgroundColor: selectedStatus ? "#EFF6FF" : "transparent",
                  "&:hover": { backgroundColor: selectedStatus ? "#DBEAFE" : "#F8FAFC" },
                }}
              >
                <Box
                  sx={{
                    width: 10, height: 10, borderRadius: "50%",
                    backgroundColor: s.bgColor || s.color || "#9CA3AF",
                    border: "1px solid rgba(0,0,0,0.1)",
                  }}
                />
                <Typography sx={{ fontSize: 12.5 }}>
                  {s.label || s.name || s.value || s.id}
                </Typography>
              </Box>
            );
          })}
          {activeStatuses.length === 0 && (
            <Typography sx={{ px: 1, py: 0.75, fontSize: 12, color: "#64748B" }}>
              No active statuses.
            </Typography>
          )}
        </Box>
      </Popover>
      <Popover
        open={Boolean(categoryAnchor)}
        anchorEl={categoryAnchor}
        onClose={() => setCategoryAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box sx={{ minWidth: 200, p: 0.75 }}>
          <Typography
            sx={{
              fontSize: 11.5, fontWeight: 700, color: "#475569",
              px: 0.75, py: 0.5, borderBottom: "1px solid #E2E8F0", mb: 0.5,
            }}
          >
            Change Contact Type
          </Typography>
          {activeCategories.map((c) => {
            const selectedCat =
              c?.id === contact?.category ||
              c?.value === contact?.category ||
              c?.name === contact?.category ||
              c?.label === contact?.category;
            return (
              <Box
                key={`cat-${c.id || c.value || c.name || c.label}`}
                onClick={() => handlePick(c)}
                sx={{
                  display: "flex", alignItems: "center", gap: 0.75,
                  px: 1, py: 0.75, borderRadius: 1,
                  fontSize: 12.5, fontWeight: selectedCat ? 700 : 500,
                  cursor: "pointer",
                  backgroundColor: selectedCat ? "#EFF6FF" : "transparent",
                  "&:hover": { backgroundColor: selectedCat ? "#DBEAFE" : "#F8FAFC" },
                }}
              >
                <Box
                  sx={{
                    width: 10, height: 10, borderRadius: "50%",
                    backgroundColor: c.bgColor || c.color || "#9CA3AF",
                    border: "1px solid rgba(0,0,0,0.1)",
                  }}
                />
                <Typography sx={{ fontSize: 12.5 }}>{c.label || c.name || c.id}</Typography>
              </Box>
            );
          })}
          {activeCategories.length === 0 && (
            <Typography sx={{ px: 1, py: 0.75, fontSize: 12, color: "#64748B" }}>
              No active contact types.
            </Typography>
          )}
        </Box>
      </Popover>
    </Box>
  );
};

export default ContactCard;
